import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createOverlayPipEffect,
  createPopArtColorBwRevealEffect,
  createPopArtComicFreezeEffect,
  createPopArtDrownInMusicEffect,
  createPopArtKineticPatternsEffect,
  EffectPreset,
  EffectRow,
  EffectType,
  initEffectUpload,
  listEffects,
  PopArtEnergy,
  PopArtPalette,
  PopArtShape,
  PopArtTypo,
  uploadToPresignedUrl,
} from "../api";

// Single source of truth for effect-type registry. Add a row + a form case
// below as each new effect type ships.
const EFFECT_TYPES: {
  id: EffectType;
  label: string;
  description: string;
}[] = [
  {
    id: "overlay-pip",
    label: "Overlay PIP",
    description:
      "Une miniature glisse dans la vidéo principale pendant N secondes. Audio overlay mute, ta voix continue.",
  },
  {
    id: "popart-kinetic-patterns",
    label: "Pop-Art · Kinetic Patterns",
    description:
      "Upload une photo (ou vidéo, on prend la 1re frame). Génère 8s de toi en N&B sur fond pop-art animé. 4 axes: palette × typo × énergie × forme.",
  },
  {
    id: "popart-comic-freeze",
    label: "Pop-Art · Comic Freeze",
    description:
      "Sujet N&B + freeze frames avec onomatopées BD (POW! BOOM! ZAP!) sur fonds Ben-Day. 8s.",
  },
  {
    id: "popart-color-bw-reveal",
    label: "Pop-Art · Color → B&W Reveal",
    description:
      "Phase 1 couleur naturelle → freeze CLICK! polaroid jaune → bascule N&B + fonds pop-art qui flickent. 8s.",
  },
  {
    id: "popart-drown-in-music",
    label: "Pop-Art · Drown in Music",
    description:
      "Sujet N&B haute contrast met un casque, 8 fonds pattern flashy (notes/dots/spiral/plaid/stars/camo) flickent au beat. 12s.",
  },
];

const PRESETS: { id: EffectPreset; label: string }[] = [
  { id: "bottom-left", label: "Bas gauche" },
  { id: "bottom-center", label: "Bas centré (défaut)" },
  { id: "bottom-right", label: "Bas droite" },
];

interface CatalogOption {
  key: string;
  uses: number;
  lastUsed: string;
}

type PickerMode = "catalog" | "upload" | "manual";

interface VideoPickerProps {
  label: string;
  value: string;
  onChange: (key: string) => void;
  catalogOptions: CatalogOption[];
  /**
   * MIME types accepted by the file input. Defaults to video only; pop-art
   * effects pass `image/*,video/*` so the user can upload a still photo.
   */
  accept?: string;
}

function VideoPicker({
  label,
  value,
  onChange,
  catalogOptions,
  accept = "video/*",
}: VideoPickerProps) {
  const [mode, setMode] = useState<PickerMode>(
    catalogOptions.length > 0 ? "catalog" : "upload"
  );
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const handleFile = useCallback(
    async (file: File) => {
      setUploading(true);
      setError(null);
      setProgress(0);
      try {
        const init = await initEffectUpload(
          file.name,
          file.type || "video/mp4"
        );
        await uploadToPresignedUrl(
          init.uploadUrl,
          file,
          file.type || "video/mp4",
          setProgress
        );
        onChange(init.s3Key);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Upload échoué");
      } finally {
        setUploading(false);
      }
    },
    [onChange]
  );

  const tabs: { id: PickerMode; label: string; disabled?: boolean }[] = [
    {
      id: "catalog",
      label: "Catalogue",
      disabled: catalogOptions.length === 0,
    },
    { id: "upload", label: "Uploader" },
    { id: "manual", label: "Coller S3 key" },
  ];

  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-charcoal">{label}</label>

      <div className="flex gap-1 text-xs">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setMode(t.id)}
            disabled={t.disabled}
            className={`rounded px-2 py-1 transition ${
              mode === t.id
                ? "bg-charcoal text-ivory"
                : "text-concrete hover:text-charcoal disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:text-concrete"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {mode === "catalog" && (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-md border border-stone bg-white px-3 py-2 font-mono text-xs text-charcoal focus:border-charcoal focus:outline-none"
        >
          <option value="">— choisir une vidéo déjà utilisée —</option>
          {catalogOptions.map((opt) => (
            <option key={opt.key} value={opt.key}>
              {opt.key.replace(/^effects-inputs\//, "")} ({opt.uses}x · dernière{" "}
              {new Date(opt.lastUsed).toLocaleDateString("fr-FR")})
            </option>
          ))}
        </select>
      )}

      {mode === "upload" && (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            disabled={uploading}
            className="whitespace-nowrap rounded-md bg-charcoal px-3 py-2 text-sm font-medium text-ivory hover:bg-black disabled:opacity-50"
          >
            {uploading ? `Upload ${progress}%` : "Choisir un fichier"}
          </button>
          {value && (
            <span className="flex-1 self-center truncate font-mono text-xs text-concrete">
              {value}
            </span>
          )}
          <input
            ref={fileInput}
            type="file"
            accept={accept}
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleFile(f);
              e.target.value = "";
            }}
          />
        </div>
      )}

      {mode === "manual" && (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="effects-inputs/<id>.mp4 ou uploads/<id>.webm"
          className="w-full rounded-md border border-stone bg-white px-3 py-2 font-mono text-xs text-charcoal focus:border-charcoal focus:outline-none"
        />
      )}

      {error && <div className="text-xs text-red-600">{error}</div>}
    </div>
  );
}

interface OverlayPipFormProps {
  catalogOptions: CatalogOption[];
  onComposed: () => void;
}

function OverlayPipForm({ catalogOptions, onComposed }: OverlayPipFormProps) {
  const [mainKey, setMainKey] = useState("");
  const [overlayKey, setOverlayKey] = useState("");
  const [preset, setPreset] = useState<EffectPreset>("bottom-center");
  const [startTime, setStartTime] = useState(3);
  const [duration, setDuration] = useState(12);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitOk, setSubmitOk] = useState(false);

  const submit = async () => {
    if (!mainKey.trim() || !overlayKey.trim()) {
      setSubmitError("Renseigne les 2 vidéos");
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    setSubmitOk(false);
    try {
      await createOverlayPipEffect({
        mainVideoKey: mainKey.trim(),
        overlayVideoKey: overlayKey.trim(),
        preset,
        startTime,
        duration,
      });
      setMainKey("");
      setOverlayKey("");
      setSubmitOk(true);
      onComposed();
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : "Échec du compose");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-4 space-y-4">
      <VideoPicker
        label="Vidéo principale (où tu parles)"
        value={mainKey}
        onChange={setMainKey}
        catalogOptions={catalogOptions}
      />
      <VideoPicker
        label="Vidéo overlay (miniature)"
        value={overlayKey}
        onChange={setOverlayKey}
        catalogOptions={catalogOptions}
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div>
          <label className="mb-2 block text-sm font-medium text-charcoal">
            Position
          </label>
          <div className="space-y-1">
            {PRESETS.map((p) => (
              <label key={p.id} className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="preset"
                  checked={preset === p.id}
                  onChange={() => setPreset(p.id)}
                />
                {p.label}
              </label>
            ))}
          </div>
        </div>
        <div>
          <label className="mb-2 block text-sm font-medium text-charcoal">
            Début (s)
          </label>
          <input
            type="number"
            value={startTime}
            onChange={(e) =>
              setStartTime(Math.max(0, parseFloat(e.target.value) || 0))
            }
            min={0}
            step={0.5}
            className="w-full rounded-md border border-stone bg-white px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="mb-2 block text-sm font-medium text-charcoal">
            Durée visible (s)
          </label>
          <input
            type="number"
            value={duration}
            onChange={(e) =>
              setDuration(Math.max(2, parseFloat(e.target.value) || 0))
            }
            min={2}
            step={0.5}
            className="w-full rounded-md border border-stone bg-white px-3 py-2 text-sm"
          />
        </div>
      </div>

      {submitError && (
        <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          {submitError}
        </div>
      )}
      {submitOk && (
        <div className="rounded-md bg-lemonade/20 px-3 py-2 text-sm text-charcoal">
          Effet en file. Va dans l&apos;onglet « Effets effectués » pour suivre
          son avancement.
        </div>
      )}

      <button
        type="button"
        onClick={() => void submit()}
        disabled={submitting}
        className="rounded-md bg-charcoal px-4 py-2 text-sm font-semibold text-ivory hover:bg-black disabled:opacity-50"
      >
        {submitting ? "Envoi…" : "Composer"}
      </button>
    </div>
  );
}

// ─── Pop-art form (drives all 4 popart-* effect types) ─────────────────────

const PALETTES: { id: PopArtPalette; label: string }[] = [
  { id: "lichtenstein", label: "Lichtenstein (primaires Ben-Day)" },
  { id: "warhol", label: "Warhol Neon (rose/orange/cyan)" },
  { id: "memphis", label: "Memphis 80s (pastel + squiggles)" },
  { id: "y2k", label: "Y2K Chrome (métallique holographique)" },
];
const TYPOS: { id: PopArtTypo; label: string }[] = [
  { id: "comic", label: "Comic (WOW BOOM POP)" },
  { id: "brand-safe", label: "Mots positifs (BOLD EPIC COOL)" },
  { id: "numbers", label: "Numbers (3-2-1 100 2026)" },
  { id: "notext", label: "No text (formes seulement)" },
];
const ENERGIES: { id: PopArtEnergy; label: string }[] = [
  { id: "calm", label: "Calm (~1 élément/s)" },
  { id: "medium", label: "Medium (~2-3 éléments/s)" },
  { id: "frantic", label: "Frantic (~5-6 éléments/s)" },
];
const SHAPES: { id: PopArtShape; label: string }[] = [
  { id: "arrows", label: "Flèches" },
  { id: "stars", label: "Étoiles" },
  { id: "circles", label: "Cercles & bulles" },
  { id: "lightning", label: "Éclairs" },
];

interface PopArtFormProps {
  effectType: EffectType;
  catalogOptions: CatalogOption[];
  onComposed: () => void;
}

function PopArtForm({ effectType, catalogOptions, onComposed }: PopArtFormProps) {
  const [sourceKey, setSourceKey] = useState("");
  const [palette, setPalette] = useState<PopArtPalette>("lichtenstein");
  const [typo, setTypo] = useState<PopArtTypo>("comic");
  const [energy, setEnergy] = useState<PopArtEnergy>("medium");
  const [shape, setShape] = useState<PopArtShape>("stars");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitOk, setSubmitOk] = useState(false);

  const showVariants = effectType === "popart-kinetic-patterns";

  const submit = async () => {
    if (!sourceKey.trim()) {
      setSubmitError("Renseigne la photo ou la vidéo source");
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    setSubmitOk(false);
    try {
      const key = sourceKey.trim();
      if (effectType === "popart-kinetic-patterns") {
        await createPopArtKineticPatternsEffect({
          sourceMediaKey: key,
          palette,
          typo,
          energy,
          shape,
        });
      } else if (effectType === "popart-comic-freeze") {
        await createPopArtComicFreezeEffect({ sourceMediaKey: key });
      } else if (effectType === "popart-color-bw-reveal") {
        await createPopArtColorBwRevealEffect({ sourceMediaKey: key });
      } else if (effectType === "popart-drown-in-music") {
        await createPopArtDrownInMusicEffect({ sourceMediaKey: key });
      }
      setSourceKey("");
      setSubmitOk(true);
      onComposed();
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : "Échec du compose");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-4 space-y-4">
      <VideoPicker
        label="Photo ou vidéo source (si vidéo, on extrait la 1re frame)"
        value={sourceKey}
        onChange={setSourceKey}
        catalogOptions={catalogOptions}
        accept="image/*,video/*"
      />

      {showVariants && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-charcoal">
              Palette
            </label>
            <select
              value={palette}
              onChange={(e) => setPalette(e.target.value as PopArtPalette)}
              className="w-full rounded-md border border-stone bg-white px-3 py-2 text-sm"
            >
              {PALETTES.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-charcoal">
              Typographie
            </label>
            <select
              value={typo}
              onChange={(e) => setTypo(e.target.value as PopArtTypo)}
              className="w-full rounded-md border border-stone bg-white px-3 py-2 text-sm"
            >
              {TYPOS.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-charcoal">
              Énergie
            </label>
            <select
              value={energy}
              onChange={(e) => setEnergy(e.target.value as PopArtEnergy)}
              className="w-full rounded-md border border-stone bg-white px-3 py-2 text-sm"
            >
              {ENERGIES.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-charcoal">
              Forme principale
            </label>
            <select
              value={shape}
              onChange={(e) => setShape(e.target.value as PopArtShape)}
              className="w-full rounded-md border border-stone bg-white px-3 py-2 text-sm"
            >
              {SHAPES.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {submitError && (
        <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          {submitError}
        </div>
      )}
      {submitOk && (
        <div className="rounded-md bg-lemonade/20 px-3 py-2 text-sm text-charcoal">
          Effet en file. Higgsfield met ~60-180s. Va dans « Effets effectués »
          pour suivre.
        </div>
      )}

      <button
        type="button"
        onClick={() => void submit()}
        disabled={submitting}
        className="rounded-md bg-charcoal px-4 py-2 text-sm font-semibold text-ivory hover:bg-black disabled:opacity-50"
      >
        {submitting ? "Envoi…" : "Générer"}
      </button>
    </div>
  );
}

export default function AdminEffects() {
  const [effectType, setEffectType] = useState<EffectType>("overlay-pip");
  const [pastEffects, setPastEffects] = useState<EffectRow[]>([]);

  const loadPast = useCallback(async () => {
    try {
      const rows = await listEffects();
      setPastEffects(rows);
    } catch {
      setPastEffects([]);
    }
  }, []);

  useEffect(() => {
    void loadPast();
  }, [loadPast]);

  // Dedupe input keys across past effects of the same type, keeping the
  // most recent createdAt as lastUsed. Each effect type uses different param
  // field names — we walk every string-typed param value and treat anything
  // that looks like an `effects-inputs/...` or `uploads/...` key as catalog.
  const catalogOptions = useMemo<CatalogOption[]>(() => {
    const map = new Map<string, { uses: number; lastUsed: string }>();
    for (const e of pastEffects) {
      if (e.effectType !== effectType) continue;
      for (const v of Object.values(e.params ?? {})) {
        if (typeof v !== "string") continue;
        if (!/^(effects-inputs|uploads)\//.test(v)) continue;
        const prev = map.get(v);
        if (prev) {
          map.set(v, {
            uses: prev.uses + 1,
            lastUsed:
              prev.lastUsed > e.createdAt ? prev.lastUsed : e.createdAt,
          });
        } else {
          map.set(v, { uses: 1, lastUsed: e.createdAt });
        }
      }
    }
    return Array.from(map.entries())
      .map(([key, v]) => ({ key, ...v }))
      .sort((a, b) => b.lastUsed.localeCompare(a.lastUsed));
  }, [pastEffects, effectType]);

  return (
    <section className="rounded-lg border border-stone bg-white p-4 md:p-6">
      <h2 className="text-lg font-bold text-charcoal">Nouvel effet</h2>

      <div className="mt-4">
        <label className="mb-2 block text-sm font-medium text-charcoal">
          Type d&apos;effet
        </label>
        <ul className="space-y-2">
          {EFFECT_TYPES.map((t) => {
            const selected = effectType === t.id;
            return (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => setEffectType(t.id)}
                  aria-pressed={selected}
                  className={`w-full rounded-lg border p-4 text-left transition ${
                    selected
                      ? "border-charcoal bg-stone/40"
                      : "border-stone bg-white hover:border-concrete"
                  }`}
                >
                  <div className="text-sm font-semibold text-charcoal">
                    {t.label}
                  </div>
                  <div className="mt-1 text-sm text-concrete">
                    {t.description}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {effectType === "overlay-pip" && (
        <OverlayPipForm
          catalogOptions={catalogOptions}
          onComposed={() => void loadPast()}
        />
      )}
      {effectType.startsWith("popart-") && (
        <PopArtForm
          effectType={effectType}
          catalogOptions={catalogOptions}
          onComposed={() => void loadPast()}
        />
      )}
    </section>
  );
}
